import React, { useState } from 'react';
import {
    Server,
    Search,
    MoreVertical,
    Edit,
    Trash2,
    Power,
    Wifi,
    WifiOff,
    Activity,
    Globe,
    Key,
    ChevronLeft,
    ChevronRight,
    Eye,
    RefreshCw,
    Crown
} from 'lucide-react';

const ServerList = ({ servers, onEdit, onDelete, onView, onTestConnection, onSetMaster, loading }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [testingId, setTestingId] = useState(null);

    // Filter servers based on search term
    const filteredServers = servers.filter(server => {
        const searchLower = searchTerm.toLowerCase();
        return (
            server.name?.toLowerCase().includes(searchLower) ||
            server.ip_address?.toLowerCase().includes(searchLower) ||
            server.credential_name?.toLowerCase().includes(searchLower) ||
            server.description?.toLowerCase().includes(searchLower)
        );
    });

    // Pagination logic
    const totalPages = Math.ceil(filteredServers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentServers = filteredServers.slice(startIndex, startIndex + itemsPerPage);

    const handleTestConnection = async (e, server) => {
        e.stopPropagation();
        setTestingId(server.id);
        await onTestConnection(server);
        setTestingId(null);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                <p>Sunucular yükleniyor...</p>
            </div>
        );
    }

    return (
        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg flex flex-col h-full">
            {/* Search Bar */}
            <div className="p-4 border-b border-white/10 bg-[#252525]">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Sunucu adı, IP veya açıklama ile ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-[#0a0e13] border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-blue-500 transition-colors"
                    />
                </div>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 bg-[#1e1e1e] text-xs font-semibold text-gray-400 uppercase tracking-wider">
                <div className="col-span-3">Sunucu Adı</div>
                <div className="col-span-2">IP Adresi</div>
                <div className="col-span-2">Kimlik</div>
                <div className="col-span-2">Durum</div>
                <div className="col-span-1">Test</div>
                <div className="col-span-2 text-right">İşlemler</div>
            </div>

            {/* Table Body */}
            <div className="flex-1 overflow-y-auto">
                {currentServers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 text-gray-500">
                        <Server className="w-12 h-12 mb-3 opacity-20" />
                        <p>Kayıtlı sunucu bulunamadı</p>
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {currentServers.map((server) => (
                            <div
                                key={server.id}
                                className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-[#252525] transition-colors group"
                                onClick={() => onView(server)}
                            >
                                {/* Name */}
                                <div className="col-span-3 flex items-center gap-3 overflow-hidden cursor-pointer">
                                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shrink-0">
                                        <Server className="w-4 h-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className="font-medium text-white truncate">{server.name}</p>
                                            {server.is_master ? (
                                                <Crown className="w-4 h-4 text-yellow-500 fill-yellow-500/20 shrink-0" title="Master Sunucu" />
                                            ) : null}
                                        </div>
                                        <p className="text-xs text-gray-500 truncate">{server.description || 'Açıklama yok'}</p>
                                    </div>
                                </div>

                                {/* IP Address */}
                                <div className="col-span-2 flex items-center gap-2 text-gray-300 font-mono text-sm">
                                    <Globe className="w-3.5 h-3.5 text-gray-500" />
                                    {server.ip_address}
                                </div>

                                {/* Credential */}
                                <div className="col-span-2 flex items-center gap-2 text-gray-300 text-sm">
                                    <Key className="w-3.5 h-3.5 text-gray-500" />
                                    {server.credential_name || <span className="text-gray-600 italic">Yok</span>}
                                </div>

                                {/* Status */}
                                <div className="col-span-2">
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${server.status === 'active'
                                        ? 'bg-green-500/10 border-green-500/20 text-green-400'
                                        : server.status === 'maintenance'
                                            ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
                                            : 'bg-red-500/10 border-red-500/20 text-red-400'
                                        }`}>
                                        {server.status === 'active' ? <Activity className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                                        {server.status === 'active' ? 'Aktif' : server.status === 'maintenance' ? 'Bakımda' : 'Pasif'}
                                    </span>
                                </div>

                                {/* Connection Test */}
                                <div className="col-span-1">
                                    <button
                                        onClick={(e) => handleTestConnection(e, server)}
                                        disabled={testingId === server.id}
                                        className={`p-1.5 rounded-lg transition-all ${testingId === server.id
                                            ? 'text-blue-400 bg-blue-500/10 cursor-wait'
                                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                                            }`}
                                        title="Bağlantıyı Test Et"
                                    >
                                        {testingId === server.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                                    </button>
                                </div>

                                {/* Actions */}
                                <div className="col-span-2 flex justify-end items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                    {!server.is_master && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onSetMaster(server); }}
                                            className="p-1.5 text-gray-400 hover:text-yellow-500 hover:bg-yellow-500/10 rounded-lg transition-colors"
                                            title="Master Yap"
                                        >
                                            <Crown className="w-4 h-4" />
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onView(server); }}
                                        className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                        title="Detaylar"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onEdit(server); }}
                                        className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors"
                                        title="Düzenle"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDelete(server); }}
                                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                        title="Sil"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="p-4 border-t border-white/10 bg-[#1e1e1e] flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                        Toplam {filteredServers.length} kayıttan {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredServers.length)} arası gösteriliyor
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-1.5 rounded-lg hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed text-gray-400"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-sm text-gray-300 font-medium px-2">
                            {currentPage} / {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="p-1.5 rounded-lg hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed text-gray-400"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServerList;
